import { type MouseEvent, memo } from "react";

import { Button } from "@components/Button";
import { Icon } from "@components/Icon";
import { type TTailwindString, tw } from "classnames";

type Props = {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onQuickAddClick: (e: MouseEvent) => void;
  quickAddButtonText?: string;
};

const styles = {
  overlay: tw(
    "absolute",
    "bottom-0",
    "left-0",
    "right-0",
    "top-0",
    "z-10",
    "flex",
    "items-end",
    "justify-end",
    "p-0",
    "pointer-events-none",
    "md:p-4"
  ),
  wrapper: tw(
    "group",
    "pointer-events-auto",
    "flex",
    "min-w-0",
    "shrink-0",
    "items-start",
    "justify-start",
    "md:h-11",
    "md:w-11",
    "md:min-w-11",
    "md:items-end",
    "md:justify-end",
    "transition-[width,padding]" as TTailwindString,
    "duration-400",
    "ease-in-out",
    "md:group-hover/card:w-full" as TTailwindString,
    "md:group-focus-within/card:w-full" as TTailwindString,
    "p-3",
    "md:p-0"
  ),
  label: tw(
    "hidden",
    "truncate",
    "md:inline-block",
    "md:min-w-0",
    "md:w-0",
    "md:-ml-2" as TTailwindString,
    "md:opacity-0",
    "overflow-hidden",
    "transition-opacity",
    "duration-400",
    "ease-in-out",
    "md:group-hover/card:opacity-100" as TTailwindString,
    "md:group-hover/card:ml-0" as TTailwindString,
    "md:group-hover/card:max-w-[12rem]" as TTailwindString,
    "md:group-hover/card:w-auto" as TTailwindString,
    "md:group-focus-within/card:opacity-100" as TTailwindString,
    "md:group-focus-within/card:ml-0" as TTailwindString,
    "md:group-focus-within/card:max-w-[12rem]" as TTailwindString,
    "md:group-focus-within/card:w-auto" as TTailwindString
  ),
  button: tw(
    "h-6",
    "w-6",
    "min-w-0",
    "max-h-6",
    "max-w-6",
    "flex-none",
    "shrink-0",
    "md:max-h-none",
    "md:max-w-none",
    "md:h-11",
    "md:min-w-11",
    "md:w-full",
    "md:overflow-hidden",
    "!bg-bone-50" as TTailwindString,
    "!border" as TTailwindString,
    "hover:!bg-char-100" as TTailwindString,
    "sm:px-2"
  ),
  icon: tw("h-4", "w-4", "shrink-0", "md:h-6", "md:w-6"),
};

const ProductCardQuickAddBase = ({
  onMouseEnter,
  onMouseLeave,
  onQuickAddClick,
  quickAddButtonText,
}: Props) => {
  return (
    <div className={styles.overlay}>
      <div
        className={styles.wrapper}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role="presentation"
      >
        <Button
          aria-label={quickAddButtonText ?? "Quick add to cart"}
          className={styles.button}
          fluid
          mtrTrack={false}
          onClick={onQuickAddClick}
          rounded
          size="large"
          type="secondary"
        >
          <Icon className={styles.icon} name="quick-add" />
          {quickAddButtonText ? (
            <span className={styles.label}>{quickAddButtonText}</span>
          ) : null}
        </Button>
      </div>
    </div>
  );
};

export const ProductCardQuickAddView = memo(ProductCardQuickAddBase);
